import Button from "@/components/Button";
import { Person } from "@/types";
import { useQuery } from "@apollo/client";
import { useParams } from "react-router-dom";
import {
  GET_CAN_REAFFILIATE_UNIVERSITY_STUDENT_QUERY,
  GET_PGM_PROGRAM_HISTORY_QUERY,
  type CanReaffiliateUniversityStudentQueryResponse
} from "./queries";
import { toFormat, isInFuture } from "@/lib/dateHelpers";
import TabLayout from "./_TabLayout";
import { MaterialNames } from "@/components/material_symbol_names";
import { useState } from "react";
import TransferModal from "./_components/TransferModal";
import ReaffiliateUniversityStudentModal from "@/features/person/components/PGMProgramTable/ReaffiliateUniversityStudentModal";
import { PGMProgramTable, type PGMMetric } from "@/features/person/components/PGMProgramTable";
import { buildPgmUniversityMetric as buildSharedPgmUniversityMetric } from "@/features/person/utils/pgm-university-metric";
import { DOMAIN, PERMISSION, useHasCapabilityBool } from "@/hooks/useCapabilities";
import { usePersonEducationAccess } from "../person/_usePersonEducationAccess";
import { pgm31aTransferCopy, pgm31aTransferLevelCd } from "./_utils/pgm31aTransfer";

type PersonData = { person: Person };

const buildPgmUniversityMetric = (person: Person | undefined): PGMMetric | null => {
  if (!person) return null;
  const metric = buildSharedPgmUniversityMetric(person);
  return metric ? (metric as PGMMetric) : null;
};

const TOOLTIP_TITLE = "PGM Program Acceptable Progress";

const getActiveProgramMessage = (years: number | null): string => {
  const yearText = years ? `${years} years` : "the required time";
  return `Enrollee has ${yearText} to complete the Professional Golf Management Program from the date of purchase (Level 1 Start Date). If the program is not completed by the expiration, the enrollee will need to start over at the Qualifying Level to elect to membership.`;
};

const getExpiredProgramMessage = (years: number | null) => {
  const yearText = years ? `${years} years` : "the required time";
  return `Enrollee has not completed the Professional Golf Management Program within ${yearText} from the date of purchase (Level 1 Start Date). The enrollee must start over at the Qualifying Level to elect to membership.`;
};

const getExpirationDateMetric = (latestPgmProgram: Person["educationLatestPgmProgram"]) => {
  if (!latestPgmProgram?.pgmProgramCompleted) {
    const expirationYears = latestPgmProgram?.programDurationYears ?? null;
    return isInFuture(latestPgmProgram?.pgmProgramExpirationDate || "")
      ? { tooltip: { supportingText: getActiveProgramMessage(expirationYears), title: TOOLTIP_TITLE } }
      : {
          tooltip: { supportingText: getExpiredProgramMessage(expirationYears), title: TOOLTIP_TITLE },
          variant: "error" as const
        };
  }
};

export default function LearningDetails() {
  const { pgaId } = useParams();
  const [showTransferModal, setShowTransferModal] = useState(false);
  const [showReaffiliateModal, setShowReaffiliateModal] = useState(false);
  const { loaded, canViewPersonEducation } = usePersonEducationAccess();
  const canManageEducationRequirements = useHasCapabilityBool({
    domain: DOMAIN.EDUCATION,
    permission: PERMISSION.manageEducationPrograms
  });
  const canDeleteEducationProgramLevels = useHasCapabilityBool({
    domain: DOMAIN.EDUCATION,
    permission: PERMISSION.deleteEducationProgramLevels
  });
  const canManageAllUniversities = useHasCapabilityBool({
    domain: DOMAIN.UNIVERSITIES,
    permission: PERMISSION.manageAllUniversities
  });
  const { data, error } = useQuery<PersonData>(GET_PGM_PROGRAM_HISTORY_QUERY, {
    variables: { pgaId },
    fetchPolicy: "cache-first",
    skip: !loaded || !canViewPersonEducation || !pgaId
  });
  const { data: reaffiliateEligibilityData } = useQuery<CanReaffiliateUniversityStudentQueryResponse>(
    GET_CAN_REAFFILIATE_UNIVERSITY_STUDENT_QUERY,
    {
      variables: { pgaId: pgaId! },
      fetchPolicy: "cache-first",
      errorPolicy: "ignore",
      skip: !loaded || !canViewPersonEducation || !pgaId || !canManageAllUniversities
    }
  );

  if (!loaded) {
    return null;
  }

  if (error) return <div>Error: {error.message}</div>;

  const personData = data?.person;
  const showReaffiliateUniversityStudentAction =
    canManageAllUniversities && reaffiliateEligibilityData?.person?.canReaffiliateUniversityStudent === true;
  const latestPgmProgram = personData?.educationLatestPgmProgram;
  const transferLevelCd = pgm31aTransferLevelCd(latestPgmProgram);
  const canTransferToAutomatedPortfolio = canManageEducationRequirements && transferLevelCd != null;
  const transferCopy = transferLevelCd ? pgm31aTransferCopy(transferLevelCd) : null;
  const defaultUniversityLegacyId = personData?.lastAttendedPgmUniversity?.legacyId;

  const uniMetric = buildPgmUniversityMetric(personData);

  const metrics = [
    ...(uniMetric ? [uniMetric] : []),
    ...(latestPgmProgram
      ? [
          {
            label: "PGM Program Type",
            value: latestPgmProgram.programType,
            iconName: "school" as MaterialNames,
            link: {
              to: "/people/:pgaId/membership/path-to-membership",
              params: { pgaId: pgaId! }
            }
          },
          {
            label: "PGM Version",
            value: latestPgmProgram.curriculumVersion,
            iconName: "school" as MaterialNames,
            ...(canTransferToAutomatedPortfolio &&
              transferCopy && {
                tooltip: {
                  title: transferCopy.tooltipTitle,
                  supportingText: "",
                  actions: {
                    primary: <Button onClick={() => setShowTransferModal(true)}>{transferCopy.actionLabel}</Button>
                  }
                }
              })
          },
          {
            label: "Completion Date",
            value: latestPgmProgram?.pgmProgramCompleted ? toFormat(latestPgmProgram.endDate || "") : null,
            iconName: "today" as MaterialNames,
            ...(latestPgmProgram?.pgmProgramCompleted && { variant: "success" as const })
          },
          {
            label: "Expiration Date",
            value: toFormat(latestPgmProgram?.pgmProgramExpirationDate || ""),
            iconName: "today" as MaterialNames,
            ...getExpirationDateMetric(latestPgmProgram)
          }
        ]
      : [])
  ] as PGMMetric[];

  return (
    <TabLayout>
      <PGMProgramTable
        key={`pgm-table-${pgaId}`}
        personId={pgaId || ""}
        actions={[
          ...(showReaffiliateUniversityStudentAction
            ? [
                {
                  label: "Re-Affiliate Student",
                  icon: "school" as MaterialNames,
                  variant: "text" as const,
                  testId: "reaffiliate-university-student",
                  onClick: () => setShowReaffiliateModal(true)
                }
              ]
            : []),
          ...(canTransferToAutomatedPortfolio && transferCopy
            ? [
                {
                  label: transferCopy.actionLabel,
                  icon: "transfer_within_a_station" as MaterialNames,
                  variant: "text" as const,
                  testId: "transfer-to-automated-portfolio",
                  onClick: () => setShowTransferModal(true)
                }
              ]
            : [])
        ]}
        testId="program-history-table"
        customMetrics={metrics}
        context="education"
        canManageEducationRequirements={canManageEducationRequirements}
        canDeleteEducationProgramLevels={canDeleteEducationProgramLevels}
      />
      {showReaffiliateModal && pgaId ? (
        <ReaffiliateUniversityStudentModal
          open
          onClose={() => setShowReaffiliateModal(false)}
          pgaId={pgaId}
          defaultUniversityLegacyId={defaultUniversityLegacyId}
        />
      ) : null}
      {showTransferModal && transferLevelCd && canManageEducationRequirements && (
        <TransferModal
          pgaId={pgaId!}
          personData={personData!}
          levelCd={transferLevelCd}
          onClosed={() => setShowTransferModal(false)}
        />
      )}
    </TabLayout>
  );
}
